import { Key, useEffect, useMemo, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router'
import { InfoCircleOutlined } from '@ant-design/icons'
import { useGetOboDetails } from '@Admin/AdminQuotingPage/components/PartDFMReportPanel/logic'
import {
    salesOrderLines_salesOrderLines_items as SalesOrderLineItemType,
    salesOrderLines_salesOrderLines_items_shipmentAndInspection as ShipmentAndInspectionType,
    salesOrderLines_salesOrderLines_items_quoteModelRevision_dfmReports as DfmReportsType,
    salesOrderLines_salesOrderLines_items_quoteModelRevision as SalesOrderLineItemQmrType,
} from '@Orders/OrderDetailsPage/Queries/__generated__/salesOrderLines'
import DataTableFileName from '@Orders/OrderDetailsPage/Status/DataTableFileName'
import DescriptionData from '@Orders/OrderDetailsPage/Status/DescriptionData'
import { ColumnPropsType } from '@Orders/OrderDetailsPage/Status/types'
import { selectFlags as selectOrderDetailsFlags } from '@Orders/OrderDetailsPage/selectors'
import { getShipmentCount } from '@Orders/OrderDetailsPage/Status/logic'
import { OrderedItemsTable } from '@Orders/OrderDetailsPage/Status/DataTables/OrderedItemsTable'
import { InspectionReportDataBtn } from '@Orders/OrderDetailsPage/OrderDetailHeader/InspectionReportData'
import ImOrderTable from '@Orders/OrderDetailsPage/Status/DataTables/ImOrderTable'
import { useQuery } from '@apollo/client'
import dfmFeedbackQuery from '@Orders/OrderDetailsPage/Status/dfmFeedbackQuery'
import { dfmFeedbackQuery_salesOrder_salesOrderLines_items as SalesOrderLineItemsType } from '@Orders/OrderDetailsPage/Status/__generated__/dfmFeedbackQuery'

import Box from '~/core/Box'
import Button from '~/core/Button'
import Text from '~/core/Text'
import Image from '~/core/Image'
import Link from '~/core/Link'
import Tooltip from '~/core/Tooltip'
import {
    TOOLING_CHANGE_PROCESSES_ID,
    TOOLING_PROCESSES_IDS,
} from '~/lib/partsConstants'
import { clearCameraConfig } from '~/redux-modules/qmr-viewer/actions'
import {
    DFM_FEEDBACK_KEY,
    INSPECTION_KEY,
    PART_DETAILS_PAGES,
    SUMMARY_KEY,
} from '~/apps/consumer/components/PartViewerModal/logic'
import PartViewerModal from '~/apps/consumer/components/PartViewerModal'
import {
    PARTIAL_SHIPPING_STATUS,
    SHIPPED_STATUS,
    PRODUCT_INPROGRESS_STATUS,
} from '~/apps/consumer/pages/Orders/constants'
import { ProcessType } from '~/apps/consumer/components/PartViewerModal/types'
import { ADMIN_PATH, ORDERS_PATH } from '~/redux-modules/preferences'
import {
    SalesOrderLineStateType,
    OrderedItemsRecordType,
    SalesOrderLinesTableType,
    OnViewInspectionButtonClickFuncArgsType,
} from '../types'
import {
    intialValues,
    PLACEMENT_PART_VIEWER,
    PLACEMENT_ORDERED_ITEMS,
    useSalesOrderLineData,
    getStatus,
    showAlertIfProcessingLineItems,
} from '../logic'

import PartStatusCell from './PartStatusCell'
import RenderShipping from './RenderShipping'
import QualityDocsAndPhotosCell from './QualityDocsAndPhotosCell'
import {
    onRowClick,
    handleTrackShipment,
    renderInspectionStatus,
    getPartShipmentDetails,
} from './logic'

type Params = {
    orderName: string
    tabs?: string
    revisionId?: string
}

const PLACEMENT_SALES_ORDER_TABLE = 'sales-order-line-item-row'
const PLACEMENT = 'sales-order-lines-table'

const SalesOrderLinesTable = (props: SalesOrderLinesTableType) => {
    const {
        cursorBasedPagination,
        isImOrder,
        isImReorder,
        numberBasedPagination,
        orderId,
        refetchVariables,
        salesOrderLines,
        setSelectedTab,
        setSelectedShipmentId,
        shippedBy,
        shipmentDetail,
        shipments: salesOrderShipmentsItem,
        shipmentIdsMap,
        isHighTouch = false,
    } = props

    const dispatch = useDispatch()
    const location = useLocation()
    const navigate = useNavigate()
    const { orderName = '', tabs, revisionId } = useParams<Params>()

    const [threeDInfo, setThreeDInfo] = useState<SalesOrderLineStateType>({
        isPartViewerModalVisible: false,
        ...intialValues,
    })
    const [shouldShowProcessingAlert, setShouldShowProcessingAlert] =
        useState(true)
    const [activePage, setActivePage] = useState('')

    const {
        isImToolingEnabled,
        isImShipmentsEnabled,
        isTransparencyImprovementEnabled,
    } = useSelector(selectOrderDetailsFlags)
    const quoteOwnerId = threeDInfo.quote?.user?.id
    const { isObo, oboUserId } = useGetOboDetails(quoteOwnerId)
    const orderDetailsPageUrl = isObo
        ? `${ADMIN_PATH}/user/${oboUserId}${ORDERS_PATH}/${orderName}`
        : `${ORDERS_PATH}/${orderName}`
    const showShipmentBtn =
        Boolean(salesOrderShipmentsItem?.length) && isImShipmentsEnabled
    const showMoldTable = isImOrder && isImToolingEnabled

    useEffect(() => {
        if (shouldShowProcessingAlert) {
            showAlertIfProcessingLineItems(salesOrderLines)
            setShouldShowProcessingAlert(false)
        }
    }, [salesOrderLines, shouldShowProcessingAlert])

    const toggleModalForPartViewer = (
        isPartViewerModalVisible: boolean,
        partData?: OrderedItemsRecordType,
        shipment?: ShipmentAndInspectionType | null,
        isDfmReportLinkClicked?: boolean,
    ) => {
        const partViewerState = {
            isPartViewerModalVisible,
            shipment,
            isDfmReportLinkClicked,
            ...partData,
        } as SalesOrderLineStateType

        const dfmReportsCount =
            partData?.dfmReports?.filter(
                (report: DfmReportsType) => report.isPublished,
            )?.length || 0
        const hasDfmReports = Boolean(dfmReportsCount) && isImOrder

        setActivePage('')
        if (partViewerState.qmrId && isPartViewerModalVisible) {
            if (hasDfmReports && isDfmReportLinkClicked) {
                navigate(`part/${partViewerState.qmrId}/${DFM_FEEDBACK_KEY}`)
            } else {
                navigate(
                    `part/${partViewerState.qmrId}/${
                        activePage ? activePage : SUMMARY_KEY
                    }`,
                )
            }
        }
        setThreeDInfo(partViewerState)
    }

    const descriptionData = (
        processName: SalesOrderLineItemType['processName'],
        partData: OrderedItemsRecordType,
    ) => {
        const toggleModal = () => {
            toggleModalForPartViewer(true, partData)
            setActivePage(PART_DETAILS_PAGES.THREADS)
        }

        const {
            materialName,
            processNames,
            holes,
            twoDdrawing,
            processes,
            material,
            quoteModelRevision,
        } = partData

        const materialPublicName = partData.material?.publicName

        return (
            <DescriptionData
                processName={processName}
                materialName={materialPublicName || materialName}
                processNames={processNames}
                processes={processes}
                material={material}
                holes={holes}
                toggleModal={toggleModal}
                twoDdrawing={twoDdrawing}
                quoteModelRevision={
                    quoteModelRevision as SalesOrderLineItemQmrType
                }
                isImOrder={isImOrder}
                isOrderStatusPage
            />
        )
    }

    const renderProductStatus = (
        productStatus: string,
        processes: ProcessType[] | null = [],
        isProductStatus: boolean | null | undefined = false,
    ) => {
        const { status, color } = getStatus(productStatus)

        const isTooling = processes?.some(
            ({ platformProcessId }) =>
                platformProcessId &&
                (TOOLING_CHANGE_PROCESSES_ID?.indexOf(platformProcessId) >= 0 ||
                    TOOLING_PROCESSES_IDS?.indexOf(platformProcessId) >= 0),
        )

        if (
            productStatus === PRODUCT_INPROGRESS_STATUS &&
            isProductStatus &&
            isTooling
        ) {
            const toolTipContent =
                'Production starts after T samples approval only.'

            return (
                <>
                    <Text color={color}>{status || '--'}</Text>
                    {!isImReorder && (
                        <Tooltip title={toolTipContent}>
                            <InfoCircleOutlined
                                style={{
                                    color: '#4B7FFC',
                                    margin: '3px',
                                    height: '14px',
                                    width: '14px',
                                }}
                            />
                        </Tooltip>
                    )}
                </>
            )
        }

        return <Text color={color}>{status || '--'}</Text>
    }

    const onViewInspectionButtonClick = ({
        evt,
        shipment,
        partData,
    }: OnViewInspectionButtonClickFuncArgsType) => {
        evt.preventDefault()
        evt.stopPropagation()
        toggleModalForPartViewer(true, partData, shipment)

        const viewInspectReportUrl = isObo
            ? `${ADMIN_PATH}/user/${oboUserId}${ORDERS_PATH}/${orderName}/part/${partData.qmrId}/${INSPECTION_KEY}`
            : `${ORDERS_PATH}/${orderName}/part/${partData.qmrId}/${INSPECTION_KEY}`

        if (partData.qmrId) {
            navigate(viewInspectReportUrl, { state: { hash: location.hash } })
        }

        setActivePage(INSPECTION_KEY)
    }

    const renderActions = (partData: OrderedItemsRecordType) => {
        const { status, shipments, processes } = partData

        const isTooling = processes.some(
            ({ platformProcessId }) =>
                platformProcessId &&
                (TOOLING_CHANGE_PROCESSES_ID?.indexOf(platformProcessId) >= 0 ||
                    TOOLING_PROCESSES_IDS?.indexOf(platformProcessId) >= 0),
        )

        const isShippingStatus =
            status === SHIPPED_STATUS || status === PARTIAL_SHIPPING_STATUS

        if (isShippingStatus && !isTooling) {
            return shipments?.map(
                shipment =>
                    shipment && (
                        <Box key={shipment?.shipmentId as Key}>
                            <Link
                                data-test-target="track-shipment"
                                name="track-shipment"
                                onClick={(evt: Event) =>
                                    handleTrackShipment({
                                        evt,
                                        shipmentId: shipment?.shipmentId || '',
                                        toggleModalForPartViewer,
                                        setSelectedTab,
                                        setSelectedShipmentId,
                                        navigate,
                                    })
                                }
                                placement={PLACEMENT_ORDERED_ITEMS}
                                style={{ marginRight: '8px' }}
                            >
                                Track shipment{' '}
                                {getShipmentCount(
                                    shipment?.shipmentId || '',
                                    shipmentIdsMap,
                                )}
                            </Link>
                            {shipment?.woAvailableToShow && (
                                <>
                                    {`|`}
                                    <Link
                                        data-test-target="view-inspection"
                                        name="view-inspection"
                                        onClick={(evt: Event) =>
                                            onViewInspectionButtonClick({
                                                evt,
                                                shipment,
                                                partData,
                                            })
                                        }
                                        placement={PLACEMENT_ORDERED_ITEMS}
                                        style={{ marginLeft: '8px' }}
                                    >
                                        View inspection
                                    </Link>
                                </>
                            )}
                        </Box>
                    ),
            )
        }
    }

    const onClose = () => {
        toggleModalForPartViewer(false)
        navigate(
            `${orderDetailsPageUrl}${
                location?.state?.hash ? `#location?.state?.hash` : ''
            }`,
        )
        dispatch(clearCameraConfig())
    }

    const ORDERED_ITEM_COLUMNS: ColumnPropsType<OrderedItemsRecordType>[] = [
        {
            title: 'Name',
            fixed: 'left',
            key: 'revision',
            width: '300px',
            render: (record: OrderedItemsRecordType) =>
                record && (
                    <Box>
                        {!isImOrder && (
                            <Image
                                style={{
                                    width: '48px',
                                    height: '48px',
                                    background: 'white',
                                    marginRight: '8px',
                                }}
                                src={record.revision?.thumbnailUri}
                            />
                        )}
                        <DataTableFileName
                            associatedParts={record?.associatedParts}
                            revision={record.revision}
                            twoDdrawing={record.twoDdrawing}
                            processes={record.processes}
                            isOrderedStatusTable
                            isImOrder={isImOrder}
                            isEnabledForIm={isImOrder}
                            dfmReports={record?.dfmReports}
                            dfmFeedback={
                                record?.quoteModelRevision?.dfmFeedback
                            }
                            qmrId={record?.qmrId}
                            orderName={orderName}
                            placement={PLACEMENT_SALES_ORDER_TABLE}
                            showMoldTag={true}
                            erpMoldId={record.erpMoldId}
                            isOrderDetailPage={true}
                            quoteUserId={record?.quote?.user.id}
                            setSelectedTab={setSelectedTab}
                        />
                    </Box>
                ),
        },
        {
            title: 'Description',
            dataIndex: 'processName',
            key: 'processName',
            width: '200px',
            render: descriptionData,
        },
        {
            title: 'Quantity',
            dataIndex: 'orderedQuantity',
            key: 'orderedQuantity',
            width: '90px',
            align: 'center',
        },
        {
            title: isTransparencyImprovementEnabled
                ? 'Part status'
                : 'Production',
            key: 'productionStatus',
            align: 'center',
            width: isTransparencyImprovementEnabled ? '200px' : '135px',
            render: record =>
                isTransparencyImprovementEnabled ? ( // TODO: check IM
                    <PartStatusCell {...{ record }} />
                ) : (
                    renderProductStatus(
                        record.productionStatus,
                        record.processes,
                        true,
                    )
                ),
        },
        {
            title: 'Inspection',
            key: 'inspectionStatus',
            align: 'center',
            width: '105px',
            render: record =>
                renderInspectionStatus(
                    record.inspectionStatus,
                    record.processes,
                    renderProductStatus,
                ),
        },
        {
            title: 'Shipping',
            key: 'shipping',
            align: 'center',
            width: '200px',
            render: (record: OrderedItemsRecordType) =>
                record && (
                    <RenderShipping
                        deliveredQuantity={record.deliveredQuantity}
                        isImOrder={isImOrder}
                        orderedQuantity={record.orderedQuantity}
                        processes={record.processes}
                        processName={record.processName}
                        shippedBy={shippedBy}
                        shipmentsData={salesOrderShipmentsItem}
                        shipments={record.shipments}
                        shipmentIdsMap={shipmentIdsMap}
                        status={record.status}
                        toggleModalForPartViewer={toggleModalForPartViewer}
                    />
                ),
        },
        {
            title: 'Actions',
            width: '280px',
            render: record => renderActions(record),
        },
    ]
    const qualityDocsAndPhotosCol = {
        title: 'Quality docs. and photos',
        key: 'qualityDocsAndPhotos',
        align: 'center' as const,
        width: '150px',
        render: (record: OrderedItemsRecordType) => (
            <QualityDocsAndPhotosCell
                {...{ record, onViewInspectionButtonClick }}
            />
        ),
    }

    if (isTransparencyImprovementEnabled) {
        ORDERED_ITEM_COLUMNS.splice(4, 1) // remove inspection status column
        ORDERED_ITEM_COLUMNS.splice(4, 0, qualityDocsAndPhotosCol)
        ORDERED_ITEM_COLUMNS.pop() //remove last actions column
    }

    const salesItems = salesOrderLines?.items || []

    const dataSourceOrderedItems = useSalesOrderLineData({
        salesOrderLines: salesItems,
    })

    const { data } = useQuery(dfmFeedbackQuery, {
        variables: { name: orderName },
    })

    const salesItemsWithDfmFeedback = data?.salesOrder?.salesOrderLines?.items

    const orderedItemDataSource = useMemo(
        () =>
            dataSourceOrderedItems?.map(item => {
                const salesItem = salesItemsWithDfmFeedback?.find(
                    (element: SalesOrderLineItemsType) => {
                        return element.id === item.id
                    },
                )

                if (item?.quoteModelRevision) {
                    item.quoteModelRevision.dfmFeedback =
                        salesItem?.quoteModelRevision?.dfmFeedback
                }
                return item
            }),
        [dataSourceOrderedItems, salesItemsWithDfmFeedback],
    )

    useEffect(() => {
        if (revisionId && !isImToolingEnabled) {
            const partData = orderedItemDataSource?.find(
                salesItem => salesItem?.qmrId === revisionId,
            )
            // if there is shipment in threeDInfo setup, then use it directly
            const shipment = threeDInfo?.shipment || partData?.shipments?.[0]

            const partViewerState = {
                isPartViewerModalVisible: true,
                shipment,
                ...partData,
            } as SalesOrderLineStateType

            setActivePage(tabs || '')

            setThreeDInfo(partViewerState)
        }

        if (threeDInfo?.dfmReports?.length > 0 && !isImToolingEnabled) {
            const partData = orderedItemDataSource?.find(
                item => item.key === threeDInfo?.key,
            )

            const partViewerState = {
                isPartViewerModalVisible: true,
                ...partData,
            } as SalesOrderLineStateType

            setThreeDInfo(partViewerState)
        }
    }, [
        isImToolingEnabled,
        orderedItemDataSource,
        revisionId,
        tabs,
        threeDInfo.dfmReports,
        threeDInfo.key,
        threeDInfo.shipment,
    ])

    return (
        <>
            <Box marginBottom="16px" marginTop={showMoldTable ? '16px' : 0}>
                <Text fontWeight={500} fontSize="20px">
                    Ordered Items
                </Text>
                {isTransparencyImprovementEnabled && (
                    <Box marginLeft="auto">
                        <InspectionReportDataBtn
                            id={orderId}
                            placement={PLACEMENT}
                            shipments={salesOrderShipmentsItem}
                        />
                        {showShipmentBtn && (
                            <Button
                                name="view-shipments"
                                data-test-target="order-view-shipments"
                                placement={PLACEMENT}
                                onClick={() => setSelectedTab('shipments')}
                                style={{ marginLeft: '8px' }}
                            >
                                View shipment(s)
                            </Button>
                        )}
                    </Box>
                )}
            </Box>
            {isImOrder && isImToolingEnabled ? (
                <ImOrderTable
                    onRowClick={onRowClick}
                    setActivePage={setActivePage}
                    salesOrderLines={salesOrderLines}
                    isImOrder={isImOrder}
                    isEnabledForIm={isImOrder}
                    orderName={orderName}
                    shippedBy={shippedBy}
                    getDescriptionData={descriptionData}
                    shipmentIdsMap={shipmentIdsMap}
                    toggleModalForPartViewer={toggleModalForPartViewer}
                    onViewInspectionButtonClick={onViewInspectionButtonClick}
                    salesOrderShipmentItems={salesOrderShipmentsItem}
                    setSelectedTab={setSelectedTab}
                />
            ) : (
                <OrderedItemsTable
                    onRowClick={onRowClick}
                    {...{
                        toggleModalForPartViewer,
                        setActivePage,
                        COLUMNS: ORDERED_ITEM_COLUMNS,
                        dataSource: orderedItemDataSource,
                        pagination:
                            isImOrder && isImToolingEnabled
                                ? numberBasedPagination
                                : cursorBasedPagination,
                    }}
                />
            )}
            {threeDInfo?.revision && (
                <PartViewerModal
                    partViewData={threeDInfo}
                    onClose={onClose}
                    activePage={activePage}
                    setActivePage={setActivePage}
                    placement={PLACEMENT_PART_VIEWER}
                    isImOrder={isImOrder}
                    isSalesOrderLine
                    isInspectionReportEnabled
                    orderId={orderId}
                    orderName={orderName}
                    refetchVariables={refetchVariables}
                    shipmentDetail={getPartShipmentDetails(
                        threeDInfo,
                        shipmentDetail,
                    )}
                    setSelectedTab={setSelectedTab}
                    isHighTouch={isHighTouch || false}
                />
            )}
        </>
    )
}

export default SalesOrderLinesTable
